import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import api, { OPERATING_GROUP_CHANGED_EVENT } from '../../api/api';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchSuppliers, createSupplier, updateSupplier, deleteSupplier,
  fetchSupplierProducts, createSupplierProduct, updateSupplierProduct,
  deleteSupplierProduct, importSupplierPriceList,
  fetchSupplierSearchProducts, invalidateSupplierDataContext, setSupplierDataGroup,
} from '../../store/slices/productSlice';
import { Supplier, SupplierBankingChange, SupplierBankingHistoryPage, SupplierBankingReviewMetrics, SupplierBankingReviewQueue, SupplierProduct, SupplierQualityAnalytics, SupplierQualityClaim, SupplierQualityIncident } from '../../types';
import SearchSelect from '../../components/SearchSelect';
import { normalizeRoleNames } from '../../utils/roleNames';
import { OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE, useSelectedOperatingGroup } from '../../hooks/useSelectedOperatingGroup';
import { isRestaurantOperatingContext } from '../../utils/operatingContext';
import { isOperatingContextReady, useOperatingContext } from '../../utils/operatingContextLifecycle';

const WEIGHT_TYPES = [
  { value: 'kg', label: 'кг' },
  { value: 'g', label: 'г' },
  { value: 'l', label: 'л' },
  { value: 'ml', label: 'мл' },
  { value: 'pcs', label: 'шт' },
];
const PAGE_SIZE = 15;
type SupplierProductSort = 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc' | 'effective-asc' | 'effective-desc' | 'code-asc' | 'code-desc';
type ClaimForm = { qualityEventId: string; claimedAmount: string; dueDate: string; ownerName: string; notes: string };

type SupplierForm = { name: string; contactInfo: string; phone: string; email: string; address: string; legalName: string; taxId: string; iban: string; bankName: string; vatRate: string; paymentTermDays: string };
const emptySupplierForm: SupplierForm = { name: '', contactInfo: '', phone: '', email: '', address: '', legalName: '', taxId: '', iban: '', bankName: '', vatRate: '0', paymentTermDays: '0' };

type SupplierBankingImportRow = {
  sourceRowNumber: number;
  supplierId: number;
  supplierName: string;
  originalLegalName: string;
  originalTaxId: string;
  originalIban: string;
  originalBankName: string;
  legalName: string;
  taxId: string;
  iban: string;
  bankName: string;
  hasChanges: boolean;
  isValid: boolean;
  errors: string[];
};

type SupplierBankingImportPreview = {
  rows: SupplierBankingImportRow[];
  readyToApplyCount: number;
  invalidCount: number;
  unchangedCount: number;
};

type SpForm = {
  supplierCode: string; supplierProductName: string; supplierWeightType: string;
  supplierUnitPrice: string; conversionFactor: string; internalProductId: string;
  isActive: boolean; notes: string;
};
const emptySpForm: SpForm = {
  supplierCode: '', supplierProductName: '', supplierWeightType: 'kg',
  supplierUnitPrice: '', conversionFactor: '1', internalProductId: '',
  isActive: true, notes: '',
};
const emptyClaimForm: ClaimForm = { qualityEventId: '', claimedAmount: '', dueDate: '', ownerName: '', notes: '' };

const SuppliersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const context = useOperatingContext();
  const authToken = useAppSelector(state => state.auth.token);
  const contextReady = isOperatingContextReady(context, authToken);
  const { groupId: selectedGroupId, changedInAnotherTab } = useSelectedOperatingGroup();
  const groupId = contextReady ? selectedGroupId : null;
  const { supplierDataGroupId, supplierDataEpoch } = useAppSelector(state => state.product);
  const [groupEventEpoch, setGroupEventEpoch] = useState(0);

  useEffect(() => {
    const operatingGroupChanged = () => {
      dispatch(invalidateSupplierDataContext());
      setGroupEventEpoch(epoch => epoch + 1);
    };
    window.addEventListener(OPERATING_GROUP_CHANGED_EVENT, operatingGroupChanged);
    return () => window.removeEventListener(OPERATING_GROUP_CHANGED_EVENT, operatingGroupChanged);
  }, [dispatch]);

  useEffect(() => {
    if (contextReady && !changedInAnotherTab && groupId !== null && supplierDataGroupId !== groupId)
      dispatch(setSupplierDataGroup(groupId));
  }, [changedInAnotherTab, contextReady, dispatch, groupId, supplierDataGroupId]);

  if (changedInAnotherTab)
    return <main className="page-shell"><p role="alert">{OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE}</p></main>;
  if (!contextReady)
    return (
      <main className="page-shell">
        <p role={context.status === 'error' || context.status === 'missing' ? 'alert' : undefined}>
          {context.status === 'error'
            ? context.error || 'Не вдалося підтвердити операційну групу.'
            : context.status === 'missing'
              ? 'Оберіть операційну групу, щоб переглянути постачальників.'
              : 'Завантаження операційного контексту...'}
        </p>
      </main>
    );
  if (supplierDataGroupId !== groupId)
    return <main className="page-shell" aria-busy="true" />;
  return (
    <SuppliersWorkspace
      key={`${groupId ?? 'unscoped'}:${context.generation}:${supplierDataEpoch}:${groupEventEpoch}`}
      groupId={groupId}
      epoch={supplierDataEpoch}
    />
  );
};

const SuppliersWorkspace: React.FC<{ groupId: string | null; epoch: number }> = ({ groupId, epoch }) => {
  const dispatch = useAppDispatch();
  const {
    suppliers: storedSuppliers, supplierProducts: storedSupplierProducts,
    supplierSearchProducts, loading, supplierDataGroupId, supplierDataEpoch,
  } = useAppSelector((s) => s.product);
  const supplierContextIsCurrent = supplierDataGroupId === groupId && supplierDataEpoch === epoch;
  const suppliers = supplierContextIsCurrent ? storedSuppliers : [];
  const supplierProducts = supplierContextIsCurrent ? storedSupplierProducts : [];
  const { username, roles, permissions } = useAppSelector((s) => s.auth);
  const normalizedRoles = normalizeRoleNames(roles);
  const canReviewBanking = normalizedRoles.some(role =>
    role === 'Керівник' || role === 'Адміністратор' || role === 'Admin');
  const [canEditSuppliers, setCanEditSuppliers] = useState(false);
  const canDeleteSupplier = isRestaurantOperatingContext(groupId) && canEditSuppliers;
  const productSearchItems = useMemo(
    () => supplierContextIsCurrent
      ? supplierSearchProducts.map((product) => ({ id: String(product.id), name: product.name }))
      : [],
    [supplierContextIsCurrent, supplierSearchProducts]
  );

  const [activeSupplierId, setActiveSupplierId] = useState<number | null>(null);
  const [editingSupplierId, setEditingSupplierId] = useState<number | null>(null);
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierDeleteError, setSupplierDeleteError] = useState('');
  const [paymentDetailsFilter, setPaymentDetailsFilter] = useState<'all' | 'ready' | 'missing' | 'pending' | 'rejected'>('all');
  const [supplierForm, setSupplierForm] = useState<SupplierForm>({ ...emptySupplierForm });
  const [editSupplierForm, setEditSupplierForm] = useState<SupplierForm>({ ...emptySupplierForm });

  const [showNewSp, setShowNewSp] = useState(false);
  const [editingSpId, setEditingSpId] = useState<number | null>(null);
  const [spForm, setSpForm] = useState<SpForm>({ ...emptySpForm });
  const [editSpForm, setEditSpForm] = useState<SpForm>({ ...emptySpForm });
  const [spSearch, setSpSearch] = useState('');
  const [spSort, setSpSort] = useState<SupplierProductSort>('name-asc');
  const [spPage, setSpPage] = useState(1);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [qualityAnalytics, setQualityAnalytics] = useState<SupplierQualityAnalytics[]>([]);
  const [qualityIncidents, setQualityIncidents] = useState<SupplierQualityIncident[]>([]);
  const [qualityClaims, setQualityClaims] = useState<SupplierQualityClaim[]>([]);
  const [bankingHistory, setBankingHistory] = useState<SupplierBankingChange[]>([]);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimForm, setClaimForm] = useState<ClaimForm>({ ...emptyClaimForm });
  const [claimError, setClaimError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const bankingFileRef = useRef<HTMLInputElement>(null);
  const [bankingPreview, setBankingPreview] = useState<SupplierBankingImportPreview | null>(null);
  const [bankingBusy, setBankingBusy] = useState(false);
  const [bankingMessage, setBankingMessage] = useState('');
  const [bankingError, setBankingError] = useState('');
  const [showBankingRegister, setShowBankingRegister] = useState(false);
  const [bankingRegister, setBankingRegister] = useState<SupplierBankingHistoryPage | null>(null);
  const [bankingRegisterSupplierId, setBankingRegisterSupplierId] = useState('');
  const [bankingRegisterSource, setBankingRegisterSource] = useState('');
  const [bankingRegisterUsername, setBankingRegisterUsername] = useState('');
  const [bankingRegisterVerificationStatus, setBankingRegisterVerificationStatus] = useState('');
  const [bankingRegisterReviewer, setBankingRegisterReviewer] = useState('');
  const [bankingRegisterFrom, setBankingRegisterFrom] = useState('');
  const [bankingRegisterTo, setBankingRegisterTo] = useState('');
  const [bankingRegisterPage, setBankingRegisterPage] = useState(1);
  const [bankingReviewQueue, setBankingReviewQueue] = useState<SupplierBankingReviewQueue | null>(null);
  const [bankingQueueStatus, setBankingQueueStatus] = useState('');
  const [bankingQueueOverdueOnly, setBankingQueueOverdueOnly] = useState(false);
  const [bankingReviewMetrics, setBankingReviewMetrics] = useState<SupplierBankingReviewMetrics | null>(null);
  const [bankingMetricsPeriodDays, setBankingMetricsPeriodDays] = useState(30);

  useEffect(() => {
    const pagePermission = 'page.management.suppliers';
    let active = true;
    setCanEditSuppliers(false);
    dispatch(fetchSuppliers({ groupId, epoch }));
    dispatch(fetchSupplierSearchProducts({ groupId, epoch }));
    dispatch(fetchSupplierProducts({ groupId, epoch }));
    api.get<{ permissions: string[]; editPermissions: string[] }>('/users/me/access')
      .then(({ data }) => {
        if (!active) return;
        setCanEditSuppliers(
          data.permissions.includes(pagePermission) &&
          data.editPermissions.includes(pagePermission) &&
          permissions.includes(pagePermission),
        );
      })
      .catch(error => {
        if (active) {
          setCanEditSuppliers(false);
          setSupplierDeleteError(error?.response?.data?.message || 'Не вдалося перевірити дозвіл на видалення постачальників.');
        }
      });
    api.get<SupplierQualityAnalytics[]>('/suppliers/quality-analytics')
      .then(response => { if (active) setQualityAnalytics(response.data); })
      .catch(() => { if (active) setQualityAnalytics([]); });
    api.get<SupplierBankingReviewQueue>('/suppliers/banking-review-queue')
      .then(response => { if (active) setBankingReviewQueue(response.data); })
      .catch(() => { if (active) setBankingReviewQueue(null); });
    api.get<SupplierBankingReviewMetrics>('/suppliers/banking-review-metrics')
      .then(response => { if (active) setBankingReviewMetrics(response.data); })
      .catch(() => { if (active) setBankingReviewMetrics(null); });
    return () => { active = false; };
  }, [dispatch, epoch, groupId, permissions]);

  const loadSupplierQuality = async (supplierId: number) => {
    const [incidentsResponse, claimsResponse, analyticsResponse] = await Promise.all([
      api.get<SupplierQualityIncident[]>(`/suppliers/${supplierId}/quality-incidents`),
      api.get<SupplierQualityClaim[]>(`/suppliers/${supplierId}/quality-claims`),
      api.get<SupplierQualityAnalytics[]>('/suppliers/quality-analytics'),
    ]);
    setQualityIncidents(incidentsResponse.data);
    setQualityClaims(claimsResponse.data);
    setQualityAnalytics(analyticsResponse.data);
  };

  useEffect(() => {
    if (!activeSupplierId) {
      setQualityIncidents([]);
      setQualityClaims([]);
      setBankingHistory([]);
      return;
    }
    Promise.all([
      loadSupplierQuality(activeSupplierId),
      api.get<SupplierBankingChange[]>(`/suppliers/${activeSupplierId}/banking-history`)
        .then(response => setBankingHistory(response.data)),
    ]).catch(() => setClaimError('Не вдалося завантажити дані постачальника.'));
  }, [activeSupplierId]);

  const activeSupplier = suppliers.find(s => s.id === activeSupplierId) ?? null;
  const activeQuality = qualityAnalytics.find(item => item.supplierId === activeSupplierId) ?? null;
  const qualityBySupplier = new Map(qualityAnalytics.map(item => [item.supplierId, item]));
  const paymentReadyCount = suppliers.filter(supplier => supplier.hasPaymentDetails).length;
  const supplierSearchQuery = supplierSearch.trim().toLocaleLowerCase('uk-UA');
  const canReviewSupplier = (supplier: Supplier) =>
    canReviewBanking
    && (supplier.bankingChangedByUsername || '').toLocaleLowerCase('uk-UA')
      !== (username || '').toLocaleLowerCase('uk-UA');
  const visibleSuppliers = suppliers.filter(supplier => {
    const matchesPaymentDetails =
      paymentDetailsFilter === 'all' ||
      (paymentDetailsFilter === 'ready' && supplier.hasPaymentDetails) ||
      (paymentDetailsFilter === 'missing' && !supplier.hasPaymentDetails) ||
      (paymentDetailsFilter === 'pending' && supplier.bankingVerificationStatus === 'PendingVerification') ||
      (paymentDetailsFilter === 'rejected' && supplier.bankingVerificationStatus === 'Rejected');
    const matchesSearch = !supplierSearchQuery || [
      supplier.name, supplier.legalName, supplier.phone, supplier.email, supplier.taxId,
    ].some(value => (value || '').toLocaleLowerCase('uk-UA').includes(supplierSearchQuery));
    return matchesPaymentDetails && matchesSearch;
  });
  const filteredSp = supplierProducts
    .filter(sp => !activeSupplierId || sp.supplierId === activeSupplierId)
    .filter(sp => !spSearch || sp.supplierProductName.toLowerCase().includes(spSearch.toLowerCase()) || sp.supplierCode.toLowerCase().includes(spSearch.toLowerCase()));
  const sortedSp = [...filteredSp].sort((a, b) => {
    switch (spSort) {
      case 'name-desc': return b.supplierProductName.localeCompare(a.supplierProductName, 'uk');
      case 'price-asc': return a.supplierUnitPrice - b.supplierUnitPrice;
      case 'price-desc': return b.supplierUnitPrice - a.supplierUnitPrice;
      case 'effective-asc': return a.effectiveUnitPrice - b.effectiveUnitPrice;
      case 'effective-desc': return b.effectiveUnitPrice - a.effectiveUnitPrice;
      case 'code-asc': return (a.supplierCode || '').localeCompare(b.supplierCode || '', 'uk');
      case 'code-desc': return (b.supplierCode || '').localeCompare(a.supplierCode || '', 'uk');
      case 'name-asc':
      default: return a.supplierProductName.localeCompare(b.supplierProductName, 'uk');
    }
  });
  const totalSpPages = Math.max(1, Math.ceil(sortedSp.length / PAGE_SIZE));
  const safeSpPage = Math.min(spPage, totalSpPages);
  const pagedSp = sortedSp.slice((safeSpPage - 1) * PAGE_SIZE, safeSpPage * PAGE_SIZE);
  const spPageStart = sortedSp.length === 0 ? 0 : (safeSpPage - 1) * PAGE_SIZE + 1;
  const spPageEnd = Math.min(safeSpPage * PAGE_SIZE, sortedSp.length);

  useEffect(() => {
    setSpPage(1);
  }, [activeSupplierId, spSearch, spSort]);

  const handleCreateSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplierForm.name.trim()) return;
    await dispatch(createSupplier({
      data: { ...supplierForm, vatRate: parseFloat(supplierForm.vatRate) || 0, paymentTermDays: parseInt(supplierForm.paymentTermDays, 10) || 0 },
      groupId,
      epoch,
    }));
    setShowNewSupplier(false);
    setSupplierForm({ ...emptySupplierForm });
  };
  const loadBankingReviewQueue = async (
    status = bankingQueueStatus,
    overdueOnly = bankingQueueOverdueOnly
  ) => {
    const response = await api.get<SupplierBankingReviewQueue>('/suppliers/banking-review-queue', {
      params: { status: status || undefined, overdueOnly },
    });
    setBankingReviewQueue(response.data);
  };
  const loadBankingReviewMetrics = async (periodDays = bankingMetricsPeriodDays) => {
    const response = await api.get<SupplierBankingReviewMetrics>('/suppliers/banking-review-metrics', {
      params: { periodDays },
    });
    setBankingReviewMetrics(response.data);
  };
  const openEditSupplier = (s: Supplier) => {
    setEditingSupplierId(s.id);
    setEditSupplierForm({ name: s.name, contactInfo: s.contactInfo, phone: s.phone, email: s.email, address: s.address, legalName: s.legalName || '', taxId: s.taxId || '', iban: s.iban || '', bankName: s.bankName || '', vatRate: String(s.vatRate ?? 0), paymentTermDays: String(s.paymentTermDays ?? 0) });
  };
  const handleUpdateSupplier = async (id: number) => {
    const supplier = suppliers.find(item => item.id === id);
    await dispatch(updateSupplier({
      id,
      data: { ...editSupplierForm, vatRate: parseFloat(editSupplierForm.vatRate) || 0, paymentTermDays: parseInt(editSupplierForm.paymentTermDays, 10) || 0, bankingVersion: supplier?.bankingVersion ?? 0 },
      groupId,
      epoch,
    }));
    const historyResponse = await api.get<SupplierBankingChange[]>(`/suppliers/${id}/banking-history`);
    setBankingHistory(historyResponse.data);
    await loadBankingReviewQueue();
    setEditingSupplierId(null);
  };
  const verifySupplierBanking = async (supplier: Supplier) => {
    const queueItem = bankingReviewQueue?.items.find(item => item.supplierId === supplier.id);
    const changedFields = queueItem?.changes.map(change => change.label).join(', ') || 'банківські реквізити';
    const confirmationText = queueItem?.requiresSensitiveConfirmation
      ? `КРИТИЧНА ЗМІНА: ${changedFields}.\n\nПідтвердіть, що IBAN та/або ЄДРПОУ/ІПН звірено з незалежним первинним документом.`
      : `Підтвердити зміни реквізитів постачальника «${supplier.name}» (${changedFields})?`;
    if (!window.confirm(confirmationText)) return;

    setBankingError('');
    try {
      await api.post(`/suppliers/${supplier.id}/verify-banking`, {
        bankingVersion: supplier.bankingVersion,
        confirmSensitiveChanges: queueItem?.requiresSensitiveConfirmation ?? false,
      });
      await dispatch(fetchSuppliers({ groupId, epoch }));
      await loadBankingReviewQueue();
      if (activeSupplierId === supplier.id) {
        const response = await api.get<SupplierBankingChange[]>(`/suppliers/${supplier.id}/banking-history`);
        setBankingHistory(response.data);
      }
      setBankingMessage(`Реквізити постачальника «${supplier.name}» підтверджено.`);
    } catch (e: any) {
      setBankingError(e?.response?.data?.message || 'Не вдалося підтвердити банківські реквізити.');
    }
  };
  const rejectSupplierBanking = async (supplier: Supplier) => {
    const reason = window.prompt('Вкажіть причину відхилення банківських реквізитів:');
    if (reason === null) return;
    if (!reason.trim()) {
      setBankingError('Причина відхилення є обов’язковою.');
      return;
    }
    setBankingError('');
    try {
      await api.post(`/suppliers/${supplier.id}/reject-banking`, {
        bankingVersion: supplier.bankingVersion,
        reason: reason.trim(),
      });
      await dispatch(fetchSuppliers({ groupId, epoch }));
      await loadBankingReviewQueue();
      if (activeSupplierId === supplier.id) {
        const response = await api.get<SupplierBankingChange[]>(`/suppliers/${supplier.id}/banking-history`);
        setBankingHistory(response.data);
      }
      setBankingMessage(`Реквізити постачальника «${supplier.name}» відхилено.`);
    } catch (e: any) {
      setBankingError(e?.response?.data?.message || 'Не вдалося відхилити банківські реквізити.');
    }
  };
  const handleDeleteSupplier = async (id: number) => {
    if (!canDeleteSupplier) return;
    if (!window.confirm('Видалити постачальника?')) return;
    setSupplierDeleteError('');
    try {
      await dispatch(deleteSupplier({ id, groupId, epoch })).unwrap();
      if (activeSupplierId === id) setActiveSupplierId(null);
    } catch (error) {
      setSupplierDeleteError(typeof error === 'string' ? error : 'Не вдалося видалити постачальника.');
    }
  };

  const downloadBankingTemplate = async () => {
    setBankingError('');
    let url: string | null = null;
    try {
      const response = await api.get('/suppliers/banking-template', { responseType: 'blob' });
      url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CRM4Chef_реквізити_постачальників_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: any) {
      setBankingError(e?.response?.data?.message || 'Не вдалося завантажити шаблон реквізитів.');
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  const previewBankingFile = async (file: File) => {
    setBankingBusy(true);
    setBankingError('');
    setBankingMessage('');
    setBankingPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<SupplierBankingImportPreview>(
        '/suppliers/banking-import-preview',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setBankingPreview(response.data);
    } catch (e: any) {
      setBankingError(e?.response?.data?.message || 'Не вдалося перевірити Excel-файл.');
    } finally {
      setBankingBusy(false);
      if (bankingFileRef.current) bankingFileRef.current.value = '';
    }
  };

  const applyBankingImport = async () => {
    if (!bankingPreview) return;
    const rows = bankingPreview.rows.filter(row => row.isValid && row.hasChanges);
    if (rows.length === 0) return;
    if (!window.confirm(`Оновити платіжні реквізити ${rows.length} постачальників?`)) return;
    setBankingBusy(true);
    setBankingError('');
    setBankingMessage('');
    try {
      const response = await api.post<{ updatedCount: number }>('/suppliers/banking-import-apply', {
        rows: rows.map(row => ({
          supplierId: row.supplierId,
          originalLegalName: row.originalLegalName,
          originalTaxId: row.originalTaxId,
          originalIban: row.originalIban,
          originalBankName: row.originalBankName,
          legalName: row.legalName,
          taxId: row.taxId,
          iban: row.iban,
          bankName: row.bankName,
        })),
      });
      setBankingMessage(`Оновлено реквізити: ${response.data.updatedCount}.`);
      setBankingPreview(null);
      await dispatch(fetchSuppliers({ groupId, epoch }));
      await loadBankingReviewQueue();
      if (activeSupplierId && rows.some(row => row.supplierId === activeSupplierId)) {
        const historyResponse = await api.get<SupplierBankingChange[]>(`/suppliers/${activeSupplierId}/banking-history`);
        setBankingHistory(historyResponse.data);
      }
    } catch (e: any) {
      const details = Array.isArray(e?.response?.data?.errors)
        ? ` ${e.response.data.errors.join(' ')}`
        : '';
      setBankingError((e?.response?.data?.message || 'Не вдалося застосувати реквізити.') + details);
    } finally {
      setBankingBusy(false);
    }
  };

  const bankingRegisterParams = (page = bankingRegisterPage) => ({
    page,
    pageSize: 50,
    supplierId: bankingRegisterSupplierId || undefined,
    source: bankingRegisterSource || undefined,
    username: bankingRegisterUsername.trim() || undefined,
    verificationStatus: bankingRegisterVerificationStatus || undefined,
    reviewer: bankingRegisterReviewer.trim() || undefined,
    from: bankingRegisterFrom || undefined,
    to: bankingRegisterTo || undefined,
  });

  const loadBankingRegister = async (page = 1) => {
    setBankingBusy(true);
    setBankingError('');
    try {
      const response = await api.get<SupplierBankingHistoryPage>('/suppliers/banking-history', {
        params: bankingRegisterParams(page),
      });
      setBankingRegister(response.data);
      setBankingRegisterPage(page);
      setShowBankingRegister(true);
    } catch (e: any) {
      setBankingError(e?.response?.data?.message || 'Не вдалося завантажити журнал реквізитів.');
    } finally {
      setBankingBusy(false);
    }
  };

  const exportBankingRegister = async () => {
    setBankingError('');
    let url: string | null = null;
    try {
      const response = await api.get('/suppliers/banking-history/export-excel', {
        params: bankingRegisterParams(1),
        responseType: 'blob',
      });
      url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CRM4Chef_історія_реквізитів_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: any) {
      setBankingError(e?.response?.data?.message || 'Не вдалося експортувати журнал реквізитів.');
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  const exportBankingReviewQueue = async () => {
    setBankingError('');
    let url: string | null = null;
    try {
      const response = await api.get('/suppliers/banking-review-queue/export-excel', {
        params: {
          status: bankingQueueStatus || undefined,
          overdueOnly: bankingQueueOverdueOnly,
        },
        responseType: 'blob',
      });
      url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CRM4Chef_черга_перевірки_реквізитів_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: any) {
      setBankingError(e?.response?.data?.message || 'Не вдалося експортувати чергу перевірки.');
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  const createClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSupplierId || !claimForm.qualityEventId || Number(claimForm.claimedAmount) <= 0) return;
    setClaimError('');
    try {
      await api.post(`/suppliers/${activeSupplierId}/quality-claims`, {
        qualityEventId: Number(claimForm.qualityEventId),
        claimedAmount: Number(claimForm.claimedAmount),
        dueDate: claimForm.dueDate ? new Date(claimForm.dueDate).toISOString() : null,
        ownerName: claimForm.ownerName,
        notes: claimForm.notes,
      });
      setClaimForm({ ...emptyClaimForm });
      setShowClaimForm(false);
      await loadSupplierQuality(activeSupplierId);
    } catch (e: any) {
      setClaimError(e?.response?.data?.message || 'Не вдалося створити претензію.');
    }
  };

  const transitionClaim = async (claim: SupplierQualityClaim, status: SupplierQualityClaim['status']) => {
    let recoveredAmount: number | null = null;
    let resolution = '';
    if (status === 'Settled') {
      const recoveredRaw = window.prompt('Фактично компенсована сума:', String(claim.claimedAmount));
      if (recoveredRaw === null) return;
      recoveredAmount = Number(recoveredRaw);
      resolution = window.prompt('Результат врегулювання:')?.trim() || '';
      if (!resolution) return;
    } else if (status === 'Rejected' || status === 'Cancelled') {
      resolution = window.prompt('Причина рішення:')?.trim() || '';
    }
    setClaimError('');
    try {
      await api.put(`/suppliers/quality-claims/${claim.id}/status`, {
        status,
        recoveredAmount,
        resolution,
      }, { headers: { 'If-Match': `"${claim.version}"` } });
      if (activeSupplierId) await loadSupplierQuality(activeSupplierId);
    } catch (e: any) {
      setClaimError(e?.response?.data?.message || 'Не вдалося змінити статус претензії.');
    }
  };

  const claimStatusLabel = (status: SupplierQualityClaim['status']) => ({
    Draft: 'Чернетка',
    Submitted: 'Надіслано',
    Accepted: 'Визнано',
    Rejected: 'Відхилено',
    Settled: 'Врегульовано',
    Cancelled: 'Скасовано',
  }[status]);

  const buildSpPayload = (form: SpForm, supplierId: number) => ({
    supplierId,
    internalProductId: form.internalProductId ? parseInt(form.internalProductId) : null,
    supplierCode: form.supplierCode,
    supplierProductName: form.supplierProductName,
    supplierWeightType: form.supplierWeightType,
    supplierUnitPrice: parseFloat(form.supplierUnitPrice) || 0,
    conversionFactor: parseFloat(form.conversionFactor) || 1,
    isActive: form.isActive,
    notes: form.notes,
  });

  const handleCreateSp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSupplierId || !spForm.supplierProductName.trim()) return;
    await dispatch(createSupplierProduct({ data: buildSpPayload(spForm, activeSupplierId), groupId, epoch }));
    dispatch(fetchSupplierProducts({ groupId, epoch }));
    setShowNewSp(false);
    setSpForm({ ...emptySpForm });
  };
  const openEditSp = (sp: SupplierProduct) => {
    setEditingSpId(sp.id);
    setEditSpForm({
      supplierCode: sp.supplierCode,
      supplierProductName: sp.supplierProductName,
      supplierWeightType: sp.supplierWeightType,
      supplierUnitPrice: String(sp.supplierUnitPrice),
      conversionFactor: String(sp.conversionFactor),
      internalProductId: sp.internalProductId != null ? String(sp.internalProductId) : '',
      isActive: sp.isActive,
      notes: sp.notes,
    });
  };
  const handleUpdateSp = async (sp: SupplierProduct) => {
    await dispatch(updateSupplierProduct({ id: sp.id, data: buildSpPayload(editSpForm, sp.supplierId), groupId, epoch }));
    dispatch(fetchSupplierProducts({ groupId, epoch }));
    setEditingSpId(null);
  };
  const handleDeleteSp = async (id: number) => {
    if (!window.confirm('Видалити позицію?')) return;
    await dispatch(deleteSupplierProduct({ id, groupId, epoch }));
  };

  const handleExport = () => {
    const rows = filteredSp.map(sp => ({
      'Код постачальника': sp.supplierCode,
      'Назва у постачальника': sp.supplierProductName,
      'Одиниця постачальника': WEIGHT_TYPES.find(w => w.value === sp.supplierWeightType)?.label ?? sp.supplierWeightType,
      'Ціна за одиницю (грн)': sp.supplierUnitPrice,
      'Коефіцієнт конвертації': sp.conversionFactor,
      'Ефективна ціна': Number(sp.effectiveUnitPrice.toFixed(4)),
      'Внутрішній продукт': sp.internalProductName ?? '',
      'Активний': sp.isActive ? 'Так' : 'Ні',
      'Примітки': sp.notes,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 18 }, { wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 18 }, { wch: 24 }, { wch: 10 }, { wch: 24 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, (activeSupplier?.name ?? 'all').slice(0, 31));
    XLSX.writeFile(wb, 'pricelist_' + (activeSupplier?.name ?? 'all').replace(/[^\w]/g, '_') + '_' + new Date().toISOString().slice(0, 10) + '.xlsx');
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeSupplierId) return;
    setImportMsg(null);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = new Uint8Array(ev.target!.result as ArrayBuffer);
        const wb2 = XLSX.read(data, { type: 'array' });
        const ws2 = wb2.Sheets[wb2.SheetNames[0]];
        const rows2: any[] = XLSX.utils.sheet_to_json(ws2, { defval: '' });
        if (!rows2.length) { setImportMsg('Файл порожній.'); return; }
        const payload = rows2.map((row: any) => ({
          supplierId: activeSupplierId,
          supplierCode: String(row['Код постачальника'] ?? '').trim(),
          supplierProductName: String(row['Назва у постачальника'] ?? '').trim(),
          supplierWeightType: (() => {
            const lbl = String(row['Одиниця постачальника'] ?? '').trim();
            return WEIGHT_TYPES.find(w => w.label === lbl || w.value === lbl)?.value ?? 'kg';
          })(),
          supplierUnitPrice: parseFloat(String(row['Ціна за одиницю (грн)'] ?? 0)) || 0,
          conversionFactor: parseFloat(String(row['Коефіцієнт конвертації'] ?? 1)) || 1,
          internalProductId: null as number | null,
          isActive: true,
          notes: String(row['Примітки'] ?? '').trim(),
        })).filter((r: any) => r.supplierProductName);
        const res = await dispatch(importSupplierPriceList({ supplierId: activeSupplierId, rows: payload, groupId, epoch })).unwrap() as any;
        dispatch(fetchSupplierProducts({ groupId, epoch }));
        setImportMsg('Імпортовано: створено ' + res.created + ', оновлено ' + res.updated);
      } catch { setImportMsg('Помилка читання файлу.'); }
      finally { if (fileRef.current) fileRef.current.value = ''; }
    };
    reader.readAsArrayBuffer(file);
  };

  type ButtonTone = 'success' | 'info' | 'warning' | 'danger' | 'blocked' | 'muted';
  const btn = (tone: ButtonTone): React.CSSProperties => ({
    background: tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone}-bg)`,
    color: tone === 'muted' ? 'var(--crm-text-muted)' : `var(--crm-${tone})`,
    border: `1px solid ${tone === 'muted' ? 'var(--crm-border)' : `var(--crm-${tone})`}`,
    borderRadius: 'var(--crm-radius-sm)',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '13px',
  });
  const inp: React.CSSProperties = { border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '6px 8px', width: '100%', boxSizing: 'border-box', fontSize: '13px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' };
  const th: React.CSSProperties = { padding: '8px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)', textAlign: 'left', fontSize: '13px', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '7px 8px', verticalAlign: 'middle', fontSize: '13px', color: 'var(--crm-text)', borderBottom: '1px solid var(--crm-table-row-border)' };
  const money = (value: number) => `${value.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;

  return (
    <div className="suppliers-page" style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', color: 'var(--crm-text)' }}>
      <h2 style={{ margin: '0 0 20px', color: 'var(--crm-text)' }}>Постачальники та Прайс-листи</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div style={{ background: 'var(--crm-success-bg)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-md)', padding: 12 }}>
          <div style={{ color: 'var(--crm-success)', fontSize: 12 }}>Готові до оплати</div>
          <div style={{ color: 'var(--crm-success)', fontWeight: 700, fontSize: 25 }}>{paymentReadyCount}</div>
        </div>
        <div style={{ background: 'var(--crm-danger-bg)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-md)', padding: 12 }}>
          <div style={{ color: 'var(--crm-danger)', fontSize: 12 }}>Потрібні реквізити</div>
          <div style={{ color: 'var(--crm-danger)', fontWeight: 700, fontSize: 25 }}>{suppliers.length - paymentReadyCount}</div>
        </div>
        <button type="button" onClick={() => setPaymentDetailsFilter('pending')} style={{ background: 'var(--crm-warning-bg)', color: 'var(--crm-text)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-md)', padding: 12, textAlign: 'left', cursor: 'pointer' }}>
          <div style={{ color: 'var(--crm-warning)', fontSize: 12 }}>Очікують перевірки</div>
          <div style={{ color: 'var(--crm-warning)', fontWeight: 700, fontSize: 25 }}>{bankingReviewQueue?.pendingCount ?? 0}</div>
          <div style={{ color: 'var(--crm-warning)', fontSize: 11 }}>Прострочено понад {bankingReviewQueue?.overdueAfterHours ?? 24} год: {bankingReviewQueue?.overdueCount ?? 0}</div>
        </button>
        <button type="button" onClick={() => setPaymentDetailsFilter('rejected')} style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-text)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-md)', padding: 12, textAlign: 'left', cursor: 'pointer' }}>
          <div style={{ color: 'var(--crm-danger)', fontSize: 12 }}>Відхилені реквізити</div>
          <div style={{ color: 'var(--crm-danger)', fontWeight: 700, fontSize: 25 }}>{bankingReviewQueue?.rejectedCount ?? 0}</div>
          <div style={{ color: 'var(--crm-danger)', fontSize: 11 }}>Потребують виправлення</div>
        </button>
      </div>
      {bankingReviewMetrics && (
        <section style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-text)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', padding: 12, marginBottom: 18 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <strong style={{ color: 'var(--crm-info)', marginRight: 'auto' }}>SLA перевірки банківських реквізитів</strong>
            <select
              value={bankingMetricsPeriodDays}
              onChange={event => setBankingMetricsPeriodDays(Number(event.target.value))}
              style={{ padding: 6 }}
            >
              <option value={7}>7 днів</option>
              <option value={30}>30 днів</option>
              <option value={90}>90 днів</option>
              <option value={365}>365 днів</option>
            </select>
            <button type="button" style={btn('info')} onClick={() => loadBankingReviewMetrics()}>
              Оновити
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 9 }}>
            <div><small>Рішень</small><div style={{ fontSize: 22, fontWeight: 700 }}>{bankingReviewMetrics.reviewedCount}</div></div>
            <div><small>У межах {bankingReviewMetrics.slaHours} год</small><div style={{ fontSize: 22, fontWeight: 700, color: bankingReviewMetrics.slaCompliancePercent >= 90 ? 'var(--crm-success)' : 'var(--crm-danger)' }}>{bankingReviewMetrics.slaCompliancePercent}%</div></div>
            <div><small>Середній час</small><div style={{ fontSize: 22, fontWeight: 700 }}>{bankingReviewMetrics.averageReviewHours} год</div></div>
            <div><small>Максимальний час</small><div style={{ fontSize: 22, fontWeight: 700 }}>{bankingReviewMetrics.maximumReviewHours} год</div></div>
            <div><small>Підтверджено / відхилено</small><div style={{ fontSize: 22, fontWeight: 700 }}>{bankingReviewMetrics.verifiedCount} / {bankingReviewMetrics.rejectedCount}</div></div>
            <div>
              <small>Атестація критичних</small>
              <div style={{ fontSize: 22, fontWeight: 700, color: bankingReviewMetrics.criticalAttestationPercent === 100 ? 'var(--crm-success)' : 'var(--crm-danger)' }}>
                {bankingReviewMetrics.criticalAttestedCount}/{bankingReviewMetrics.criticalReviewCount}
              </div>
            </div>
          </div>
          {bankingReviewMetrics.reviewers.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--crm-info)', fontWeight: 600 }}>Результати за перевіряльниками</summary>
              <div style={{ overflowX: 'auto', marginTop: 7 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)' }}>
                  <thead><tr>
                    <th style={th}>Перевіряльник</th><th style={th}>Рішень</th><th style={th}>Підтверджено</th>
                    <th style={th}>Відхилено</th><th style={th}>Середній час</th><th style={th}>SLA</th>
                  </tr></thead>
                  <tbody>{bankingReviewMetrics.reviewers.map(reviewer => (
                    <tr key={reviewer.reviewer}>
                      <td style={td}>{reviewer.reviewer}</td><td style={td}>{reviewer.reviewedCount}</td>
                      <td style={td}>{reviewer.verifiedCount}</td><td style={td}>{reviewer.rejectedCount}</td>
                      <td style={td}>{reviewer.averageReviewHours} год</td><td style={td}>{reviewer.slaCompliancePercent}%</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </details>
          )}
        </section>
      )}
      {((bankingReviewQueue?.pendingCount ?? 0) + (bankingReviewQueue?.rejectedCount ?? 0)) > 0 && (
        <section style={{ background: 'var(--crm-warning-bg)', color: 'var(--crm-text)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-md)', padding: 12, marginBottom: 18 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ color: 'var(--crm-warning)', marginRight: 'auto' }}>Черга контролю банківських реквізитів</strong>
            <select
              value={bankingQueueStatus}
              onChange={event => setBankingQueueStatus(event.target.value)}
              style={{ padding: 6 }}
            >
              <option value="">Очікують і відхилені</option>
              <option value="PendingVerification">Лише очікують</option>
              <option value="Rejected">Лише відхилені</option>
            </select>
            <label style={{ fontSize: 12 }}>
              <input
                type="checkbox"
                checked={bankingQueueOverdueOnly}
                onChange={event => setBankingQueueOverdueOnly(event.target.checked)}
              />{' '}
              лише прострочені
            </label>
            <button type="button" style={btn('warning')} onClick={() => loadBankingReviewQueue()}>Застосувати</button>
            <button type="button" style={btn('success')} onClick={exportBankingReviewQueue}>Excel</button>
          </div>
          {bankingReviewQueue?.oldestPendingAtUtc && (
            <div style={{ color: 'var(--crm-text-muted)', fontSize: 12, marginTop: 5 }}>
              Найстаріша заявка: {new Date(bankingReviewQueue.oldestPendingAtUtc).toLocaleString('uk-UA')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', color: 'var(--crm-text)', fontSize: 12, marginTop: 5 }}>
            <span>Заблокована незапланована кредиторка: <strong>{money(bankingReviewQueue?.pendingUnplannedAccountsPayable ?? 0)}</strong></span>
            <span>Із неї прострочено: <strong style={{ color: 'var(--crm-danger)' }}>{money(bankingReviewQueue?.overdueUnplannedAccountsPayable ?? 0)}</strong></span>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 260, marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)' }}>
              <thead><tr>
                <th style={th}>Постачальник</th><th style={th}>Статус</th><th style={th}>Автор зміни</th>
                <th style={th}>Вік</th><th style={th}>Що змінено</th><th style={th}>Фінансовий ризик</th><th style={th}>Рішення</th>
              </tr></thead>
              <tbody>
                {bankingReviewQueue?.items.map(item => {
                  const supplier = suppliers.find(candidate => candidate.id === item.supplierId);
                  return (
                    <tr key={item.supplierId} style={{ background: item.isOverdue ? 'var(--crm-danger-bg)' : undefined }}>
                      <td style={td}><button type="button" onClick={() => setActiveSupplierId(item.supplierId)} style={{ border: 0, background: 'transparent', color: 'var(--crm-info)', cursor: 'pointer', fontWeight: 600 }}>{item.supplierName}</button></td>
                      <td style={td}>
                        {item.status === 'PendingVerification' ? 'Очікує перевірки' : 'Відхилено'}
                        <div style={{
                          fontSize: 11,
                          color: item.riskLevel === 'Critical' ? 'var(--crm-danger)' : item.riskLevel === 'High' ? 'var(--crm-warning)' : 'var(--crm-text-muted)',
                          fontWeight: item.riskLevel === 'Standard' ? 400 : 700,
                        }}>
                          {item.riskLevel === 'Critical' ? 'Критичний ризик' : item.riskLevel === 'High' ? 'Високий ризик' : 'Стандартний ризик'}
                        </div>
                      </td>
                      <td style={td}>{item.changedByUsername || 'Система'}{item.changedAtUtc ? <div style={{ fontSize: 11 }}>{new Date(item.changedAtUtc).toLocaleString('uk-UA')}</div> : null}</td>
                      <td style={{ ...td, color: item.isOverdue ? 'var(--crm-danger)' : undefined }}>{item.ageHours} год{item.isOverdue ? ' · прострочено' : ''}</td>
                      <td style={{ ...td, minWidth: 260 }}>
                        {item.changes.length > 0 ? (
                          <details>
                            <summary style={{ cursor: 'pointer', color: 'var(--crm-info)', fontWeight: 600 }}>
                              {item.changes.length} {item.changes.length === 1 ? 'поле' : 'поля'}
                            </summary>
                            <div style={{ marginTop: 5 }}>
                              {item.changes.map(change => (
                                <div key={change.field} style={{ marginBottom: 5 }}>
                                  <strong>{change.label}:</strong>
                                  <div style={{ color: 'var(--crm-text-muted)', textDecoration: 'line-through' }}>{change.previousValue}</div>
                                  <div style={{ color: 'var(--crm-success)' }}>→ {change.newValue}</div>
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : <span style={{ color: 'var(--crm-text-muted)' }}>Історія недоступна</span>}
                      </td>
                      <td style={td}>
                        <strong>{money(item.unplannedAccountsPayable)}</strong>
                        <div style={{ fontSize: 11, color: item.overdueUnplannedAccountsPayable > 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>
                          {item.unplannedReceiptCount} накл. · прострочено {money(item.overdueUnplannedAccountsPayable)}
                        </div>
                        {item.nearestPaymentDueDate ? <div style={{ fontSize: 11 }}>Найближчий строк: {new Date(item.nearestPaymentDueDate).toLocaleDateString('uk-UA')}</div> : null}
                      </td>
                      <td style={td}>
                        {item.status === 'PendingVerification' && supplier && canReviewSupplier(supplier) ? (
                          <div style={{ display: 'flex', gap: 5 }}>
                            <button style={{ ...btn('blocked'), padding: '4px 8px' }} onClick={() => verifySupplierBanking(supplier)}>Підтвердити</button>
                            <button style={{ ...btn('danger'), padding: '4px 8px' }} onClick={() => rejectSupplierBanking(supplier)}>Відхилити</button>
                          </div>
                        ) : item.status === 'PendingVerification' && supplier && canReviewBanking ? (
                          <span style={{ color: 'var(--crm-warning)' }}>Потрібен інший перевіряльник</span>
                        ) : item.status === 'PendingVerification' ? (
                          <span style={{ color: 'var(--crm-text-muted)' }}>Рішення доступне керівнику або адміністратору</span>
                        ) : (
                          <span style={{ color: 'var(--crm-danger)' }}>{item.rejectionReason || 'Потрібне виправлення'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {bankingReviewQueue?.items.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Записів за фільтром немає</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <section style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-text)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', padding: 12, marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ color: 'var(--crm-info)', marginRight: 'auto' }}>Масове заповнення платіжних реквізитів</strong>
          <button type="button" style={btn('muted')} onClick={() => showBankingRegister ? setShowBankingRegister(false) : loadBankingRegister(1)}>
            {showBankingRegister ? 'Закрити журнал' : 'Журнал змін'}
          </button>
          <button type="button" style={btn('info')} onClick={downloadBankingTemplate}>Завантажити Excel-шаблон</button>
          <button type="button" style={btn('blocked')} onClick={() => bankingFileRef.current?.click()} disabled={bankingBusy}>
            {bankingBusy ? 'Перевірка...' : 'Перевірити заповнений Excel'}
          </button>
          <input
            ref={bankingFileRef}
            type="file"
            accept=".xlsx,.xlsm"
            style={{ display: 'none' }}
            onChange={event => {
              const file = event.target.files?.[0];
              if (file) previewBankingFile(file);
            }}
          />
        </div>
        <div style={{ color: 'var(--crm-text-muted)', fontSize: 12, marginTop: 6 }}>
          Спочатку завантажте шаблон із поточними даними. Запис у базу виконується лише після preview і окремого підтвердження.
        </div>
        {bankingError && <div role="alert" style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: 8, borderRadius: 'var(--crm-radius-sm)', marginTop: 8 }}>{bankingError}</div>}
        {bankingMessage && <div role="status" style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', padding: 8, borderRadius: 'var(--crm-radius-sm)', marginTop: 8 }}>{bankingMessage}</div>}
        {showBankingRegister && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--crm-border)', paddingTop: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap', marginBottom: 10 }}>
              <label style={{ fontSize: 12 }}>Постачальник
                <SearchSelect
                  value={bankingRegisterSupplierId}
                  onChange={setBankingRegisterSupplierId}
                  items={[
                    { id: '', name: 'Усі постачальники' },
                    ...suppliers.map(supplier => ({ id: String(supplier.id), name: supplier.name })),
                  ]}
                  placeholder="Усі постачальники"
                />
              </label>
              <label style={{ fontSize: 12 }}>Джерело
                <select value={bankingRegisterSource} onChange={event => setBankingRegisterSource(event.target.value)} style={{ display: 'block', padding: 6 }}>
                  <option value="">Усі</option>
                  <option value="Manual">Ручна зміна</option>
                  <option value="ExcelImport">Excel-імпорт</option>
                </select>
              </label>
              <label style={{ fontSize: 12 }}>Користувач
                <input value={bankingRegisterUsername} onChange={event => setBankingRegisterUsername(event.target.value)} style={{ display: 'block', padding: 6 }} />
              </label>
              <label style={{ fontSize: 12 }}>Результат
                <select value={bankingRegisterVerificationStatus} onChange={event => setBankingRegisterVerificationStatus(event.target.value)} style={{ display: 'block', padding: 6 }}>
                  <option value="">Усі</option>
                  <option value="PendingVerification">Очікує перевірки</option>
                  <option value="Verified">Підтверджено</option>
                  <option value="Rejected">Відхилено</option>
                  <option value="NotRequired">Не вимагалося</option>
                </select>
              </label>
              <label style={{ fontSize: 12 }}>Перевіряльник
                <input value={bankingRegisterReviewer} onChange={event => setBankingRegisterReviewer(event.target.value)} style={{ display: 'block', padding: 6 }} />
              </label>
              <label style={{ fontSize: 12 }}>Від
                <input type="date" value={bankingRegisterFrom} onChange={event => setBankingRegisterFrom(event.target.value)} style={{ display: 'block', padding: 6 }} />
              </label>
              <label style={{ fontSize: 12 }}>До
                <input type="date" value={bankingRegisterTo} onChange={event => setBankingRegisterTo(event.target.value)} style={{ display: 'block', padding: 6 }} />
              </label>
              <button type="button" style={btn('info')} onClick={() => loadBankingRegister(1)} disabled={bankingBusy}>Застосувати</button>
              <button type="button" style={btn('success')} onClick={exportBankingRegister}>Excel</button>
            </div>
            <div style={{ color: 'var(--crm-text-muted)', fontSize: 12, marginBottom: 6 }}>
              Записів: <strong>{bankingRegister?.total ?? 0}</strong>
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 420 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)' }}>
                <thead><tr>
                  <th style={th}>Дата</th><th style={th}>Постачальник</th><th style={th}>Користувач</th>
                  <th style={th}>Джерело</th><th style={th}>Попередні реквізити</th><th style={th}>Нові реквізити</th><th style={th}>Рішення</th>
                </tr></thead>
                <tbody>
                  {bankingRegister?.items.map(change => (
                    <tr key={change.id}>
                      <td style={td}>{new Date(change.changedAtUtc).toLocaleString('uk-UA')}</td>
                      <td style={td}><strong>{change.supplierName}</strong><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>ID {change.supplierId}</div></td>
                      <td style={td}>{change.changedByUsername || 'Система'}</td>
                      <td style={td}>{change.source === 'ExcelImport' ? 'Excel-імпорт' : 'Ручна зміна'}</td>
                      <td style={{ ...td, fontSize: 12 }}>
                        <div>{change.previousLegalName || '—'}</div><div>{change.previousTaxId || '—'}</div>
                        <div style={{ wordBreak: 'break-all' }}>{change.previousIban || '—'}</div><div>{change.previousBankName || '—'}</div>
                      </td>
                      <td style={{ ...td, fontSize: 12 }}>
                        <div>{change.newLegalName || '—'}</div><div>{change.newTaxId || '—'}</div>
                        <div style={{ wordBreak: 'break-all' }}>{change.newIban || '—'}</div><div>{change.newBankName || '—'}</div>
                      </td>
                      <td style={{ ...td, fontSize: 12 }}>
                        <div>{change.verificationStatus === 'Verified' ? 'Підтверджено' : change.verificationStatus === 'Rejected' ? 'Відхилено' : change.verificationStatus === 'PendingVerification' ? 'Очікує перевірки' : 'Не вимагалося'}</div>
                        {change.reviewedByUsername ? <div>{change.reviewedByUsername}</div> : null}
                        {change.reviewedAtUtc ? <div>{new Date(change.reviewedAtUtc).toLocaleString('uk-UA')}</div> : null}
                        {change.reviewedAtUtc ? (
                          <div style={{ color: change.reviewRiskLevel === 'Critical' ? 'var(--crm-danger)' : change.reviewRiskLevel === 'High' ? 'var(--crm-warning)' : 'var(--crm-text-muted)' }}>
                            Ризик: {change.reviewRiskLevel === 'Critical' ? 'критичний' : change.reviewRiskLevel === 'High' ? 'високий' : 'стандартний'}
                            {change.sensitiveChangesConfirmed ? ' · критичні поля звірено' : ''}
                          </div>
                        ) : null}
                        {change.rejectionReason ? <div style={{ color: 'var(--crm-danger)' }}>{change.rejectionReason}</div> : null}
                      </td>
                    </tr>
                  ))}
                  {bankingRegister?.items.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Записів за фільтром немає</td></tr>}
                </tbody>
              </table>
            </div>
            {(bankingRegister?.totalPages ?? 0) > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <button type="button" style={btn('muted')} disabled={bankingRegisterPage <= 1} onClick={() => loadBankingRegister(bankingRegisterPage - 1)}>←</button>
                <span>Сторінка {bankingRegisterPage} з {bankingRegister?.totalPages}</span>
                <button type="button" style={btn('muted')} disabled={bankingRegisterPage >= (bankingRegister?.totalPages ?? 1)} onClick={() => loadBankingRegister(bankingRegisterPage + 1)}>→</button>
              </div>
            )}
          </div>
        )}
        {bankingPreview && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ color: 'var(--crm-success)' }}>Готово до оновлення: <strong>{bankingPreview.readyToApplyCount}</strong></span>
              <span style={{ color: 'var(--crm-danger)' }}>Помилки: <strong>{bankingPreview.invalidCount}</strong></span>
              <span style={{ color: 'var(--crm-text-muted)' }}>Без змін: <strong>{bankingPreview.unchangedCount}</strong></span>
              <button
                type="button"
                style={{ ...btn('success'), marginLeft: 'auto', opacity: bankingPreview.readyToApplyCount === 0 ? .5 : 1 }}
                disabled={bankingBusy || bankingPreview.readyToApplyCount === 0}
                onClick={applyBankingImport}
              >
                Застосувати валідні зміни
              </button>
              <button type="button" style={btn('muted')} onClick={() => setBankingPreview(null)}>Закрити preview</button>
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 360 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)' }}>
                <thead><tr>
                  <th style={th}>Рядок</th><th style={th}>Постачальник</th><th style={th}>Юридична назва</th>
                  <th style={th}>ЄДРПОУ / ІПН</th><th style={th}>IBAN</th><th style={th}>Банк</th><th style={th}>Результат</th>
                </tr></thead>
                <tbody>
                  {bankingPreview.rows.map(row => (
                    <tr key={`${row.sourceRowNumber}-${row.supplierId}`} style={{ background: !row.isValid ? 'var(--crm-danger-bg)' : row.hasChanges ? 'var(--crm-success-bg)' : undefined }}>
                      <td style={td}>{row.sourceRowNumber}</td>
                      <td style={td}><strong>{row.supplierName}</strong>{row.supplierId > 0 && <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>ID {row.supplierId}</div>}</td>
                      <td style={td}>{row.legalName || '—'}</td>
                      <td style={td}>{row.taxId || '—'}</td>
                      <td style={{ ...td, wordBreak: 'break-all' }}>{row.iban || '—'}</td>
                      <td style={td}>{row.bankName || '—'}</td>
                      <td style={{ ...td, color: !row.isValid ? 'var(--crm-danger)' : row.hasChanges ? 'var(--crm-success)' : 'var(--crm-text-muted)', minWidth: 210 }}>
                        {!row.isValid ? row.errors.join(' ') : row.hasChanges ? 'Валідна зміна' : 'Без змін'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
      {qualityAnalytics.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 18 }}>
          <div style={{ background: 'var(--crm-info-bg)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', padding: 12 }}>
            <div style={{ color: 'var(--crm-info)', fontSize: 12 }}>Середня якість</div>
            <div style={{ color: 'var(--crm-info)', fontWeight: 700, fontSize: 25 }}>
              {(qualityAnalytics.reduce((sum, item) => sum + item.qualityScore, 0) / qualityAnalytics.length).toFixed(1)}
            </div>
          </div>
          <div style={{ background: 'var(--crm-danger-bg)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-md)', padding: 12 }}>
            <div style={{ color: 'var(--crm-danger)', fontSize: 12 }}>Інциденти якості</div>
            <div style={{ color: 'var(--crm-danger)', fontWeight: 700, fontSize: 25 }}>{qualityAnalytics.reduce((sum, item) => sum + item.incidentCount, 0)}</div>
          </div>
          <div style={{ background: 'var(--crm-blocked-bg)', border: '1px solid var(--crm-blocked)', borderRadius: 'var(--crm-radius-md)', padding: 12 }}>
            <div style={{ color: 'var(--crm-blocked)', fontSize: 12 }}>Заблокована вартість</div>
            <div style={{ color: 'var(--crm-blocked)', fontWeight: 700, fontSize: 25 }}>{qualityAnalytics.reduce((sum, item) => sum + item.currentBlockedValue, 0).toFixed(2)}</div>
            <div style={{ color: 'var(--crm-blocked)', fontSize: 11 }}>грн</div>
          </div>
        </div>
      )}
      <div className="suppliers-content-layout" style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        <div className="suppliers-list-panel" style={{ width: '300px', flexShrink: 0 }}>
          {supplierDeleteError && (
            <div role="alert" style={{ color: 'var(--crm-danger)', marginBottom: 10 }}>
              {supplierDeleteError}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <strong style={{ color: 'var(--crm-text)' }}>Постачальники</strong>
            <button style={btn('success')} onClick={() => { setShowNewSupplier(v => !v); setEditingSupplierId(null); }}>+ Додати</button>
          </div>
          <input
            type="search"
            aria-label="Пошук постачальників"
            placeholder="🔍 Назва, телефон, email, ЄДРПОУ..."
            value={supplierSearch}
            onChange={event => setSupplierSearch(event.target.value)}
            style={{ ...inp, marginBottom: 10 }}
          />
          <select
            value={paymentDetailsFilter}
            onChange={event => {
              setPaymentDetailsFilter(event.target.value as 'all' | 'ready' | 'missing' | 'pending' | 'rejected');
              setActiveSupplierId(null);
            }}
            style={{ ...inp, marginBottom: 10 }}
          >
            <option value="all">Усі постачальники</option>
            <option value="missing">Без платіжних реквізитів</option>
            <option value="pending">Очікують перевірки</option>
            <option value="rejected">Відхилені реквізити</option>
            <option value="ready">Готові до оплати</option>
          </select>
          {showNewSupplier && (
            <form onSubmit={handleCreateSupplier} style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <input style={inp} placeholder='Назва *' value={supplierForm.name} onChange={e => setSupplierForm(f => ({ ...f, name: e.target.value }))} required />
              <input style={inp} placeholder='Контактна особа' value={supplierForm.contactInfo} onChange={e => setSupplierForm(f => ({ ...f, contactInfo: e.target.value }))} />
              <input style={inp} placeholder='Телефон' value={supplierForm.phone} onChange={e => setSupplierForm(f => ({ ...f, phone: e.target.value }))} />
              <input style={inp} placeholder='Email' value={supplierForm.email} onChange={e => setSupplierForm(f => ({ ...f, email: e.target.value }))} />
              <input style={inp} placeholder='Адреса' value={supplierForm.address} onChange={e => setSupplierForm(f => ({ ...f, address: e.target.value }))} />
              <input style={inp} placeholder='Юридична назва' value={supplierForm.legalName} onChange={e => setSupplierForm(f => ({ ...f, legalName: e.target.value }))} />
              <input style={inp} placeholder='ЄДРПОУ / ІПН' value={supplierForm.taxId} onChange={e => setSupplierForm(f => ({ ...f, taxId: e.target.value }))} />
              <input style={inp} placeholder='IBAN (UA + 27 цифр)' value={supplierForm.iban} onChange={e => setSupplierForm(f => ({ ...f, iban: e.target.value.toUpperCase() }))} />
              <input style={inp} placeholder='Назва банку' value={supplierForm.bankName} onChange={e => setSupplierForm(f => ({ ...f, bankName: e.target.value }))} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: 'var(--crm-text-muted)', whiteSpace: 'nowrap' }}>ПДВ %:</label>
                <input type='number' min='0' max='100' step='0.01' style={{ ...inp, width: '90px' }} placeholder='0' value={supplierForm.vatRate} onChange={e => setSupplierForm(f => ({ ...f, vatRate: e.target.value }))} />
                <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>(0 = без ПДВ)</span>
              </div>
              <label style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>Відстрочка оплати, днів:
                <input type='number' min='0' max='365' step='1' style={{ ...inp, marginTop: 3 }} value={supplierForm.paymentTermDays} onChange={e => setSupplierForm(f => ({ ...f, paymentTermDays: e.target.value }))} />
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type='submit' style={btn('success')}>Зберегти</button>
                <button type='button' style={btn('muted')} onClick={() => setShowNewSupplier(false)}>Скасувати</button>
              </div>
            </form>
          )}
          {loading && <div style={{ color: 'var(--crm-text-muted)', textAlign: 'center', padding: '20px' }}>Завантаження…</div>}
          {visibleSuppliers.map(s => (
            <div key={s.id}
              style={{ border: `2px solid ${activeSupplierId === s.id ? 'var(--crm-info)' : 'var(--crm-border)'}`, borderRadius: 'var(--crm-radius-md)', padding: '10px', marginBottom: '8px', background: activeSupplierId === s.id ? 'var(--crm-info-bg)' : 'var(--crm-surface)', cursor: 'pointer' }}
              onClick={() => { setActiveSupplierId(s.id); setEditingSupplierId(null); setShowNewSp(false); }}
            >
              {editingSupplierId === s.id ? (
                <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <input style={inp} value={editSupplierForm.name} onChange={e => setEditSupplierForm(f => ({ ...f, name: e.target.value }))} placeholder='Назва' />
                  <input style={inp} value={editSupplierForm.contactInfo} onChange={e => setEditSupplierForm(f => ({ ...f, contactInfo: e.target.value }))} placeholder='Контакт' />
                  <input style={inp} value={editSupplierForm.phone} onChange={e => setEditSupplierForm(f => ({ ...f, phone: e.target.value }))} placeholder='Телефон' />
                  <input style={inp} value={editSupplierForm.email} onChange={e => setEditSupplierForm(f => ({ ...f, email: e.target.value }))} placeholder='Email' />
                  <input style={inp} value={editSupplierForm.address} onChange={e => setEditSupplierForm(f => ({ ...f, address: e.target.value }))} placeholder='Адреса' />
                  <input style={inp} value={editSupplierForm.legalName} onChange={e => setEditSupplierForm(f => ({ ...f, legalName: e.target.value }))} placeholder='Юридична назва' />
                  <input style={inp} value={editSupplierForm.taxId} onChange={e => setEditSupplierForm(f => ({ ...f, taxId: e.target.value }))} placeholder='ЄДРПОУ / ІПН' />
                  <input style={inp} value={editSupplierForm.iban} onChange={e => setEditSupplierForm(f => ({ ...f, iban: e.target.value.toUpperCase() }))} placeholder='IBAN (UA + 27 цифр)' />
                  <input style={inp} value={editSupplierForm.bankName} onChange={e => setEditSupplierForm(f => ({ ...f, bankName: e.target.value }))} placeholder='Назва банку' />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '13px', color: 'var(--crm-text-muted)', whiteSpace: 'nowrap' }}>ПДВ %:</label>
                    <input type='number' min='0' max='100' step='0.01' style={{ ...inp, width: '90px' }} value={editSupplierForm.vatRate} onChange={e => setEditSupplierForm(f => ({ ...f, vatRate: e.target.value }))} />
                  </div>
                  <label style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>Відстрочка оплати, днів:
                    <input type='number' min='0' max='365' step='1' style={{ ...inp, marginTop: 3 }} value={editSupplierForm.paymentTermDays} onChange={e => setEditSupplierForm(f => ({ ...f, paymentTermDays: e.target.value }))} />
                  </label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button style={btn('info')} onClick={() => handleUpdateSupplier(s.id)}>Зберегти</button>
                    <button style={btn('muted')} onClick={() => setEditingSupplierId(null)}>✕</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontWeight: 600, color: 'var(--crm-text)', marginBottom: '4px' }}>{s.name}</div>
                  {s.phone && <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>Тел: {s.phone}</div>}
                  {s.email && <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>✉️ {s.email}</div>}
                  {s.legalName && <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>{s.legalName}</div>}
                  {s.taxId && <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>ЄДРПОУ/ІПН: {s.taxId}</div>}
                  {s.iban && <div style={{ fontSize: '11px', color: 'var(--crm-info)', wordBreak: 'break-all' }}>{s.iban}</div>}
                  <div style={{ fontSize: '11px', color: s.hasPaymentDetails ? 'var(--crm-success)' : 'var(--crm-danger)', marginTop: 3 }}>
                    {s.hasPaymentDetails ? 'Готовий до оплати' : s.paymentDetailsIssue}
                  </div>
                  {s.bankingVerificationStatus === 'PendingVerification' && (
                    <div style={{ marginTop: 5 }}>
                      <div style={{ fontSize: 11, color: 'var(--crm-warning)' }}>
                        Змінив: {s.bankingChangedByUsername || 'невідомий користувач'}
                        {s.bankingChangedAtUtc ? ` · ${new Date(s.bankingChangedAtUtc).toLocaleString('uk-UA')}` : ''}
                      </div>
                      {canReviewSupplier(s) ? <div style={{ display: 'flex', gap: 5 }}>
                        <button
                          style={{ ...btn('blocked'), padding: '4px 8px', fontSize: 12, marginTop: 4 }}
                          onClick={e => { e.stopPropagation(); verifySupplierBanking(s); }}
                        >
                          Підтвердити
                        </button>
                        <button
                          style={{ ...btn('danger'), padding: '4px 8px', fontSize: 12, marginTop: 4 }}
                          onClick={e => { e.stopPropagation(); rejectSupplierBanking(s); }}
                        >
                          Відхилити
                        </button>
                      </div> : (
                        <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginTop: 4 }}>
                          {canReviewBanking ? 'Потрібен інший перевіряльник' : 'Рішення доступне керівнику або адміністратору'}
                        </div>
                      )}
                    </div>
                  )}
                  {s.bankingVerificationStatus === 'Verified' && (
                    <div style={{ fontSize: 11, color: 'var(--crm-success)', marginTop: 3 }}>
                      Перевірив: {s.bankingVerifiedByUsername || 'уповноважений користувач'}
                      {s.bankingVerifiedAtUtc ? ` · ${new Date(s.bankingVerifiedAtUtc).toLocaleString('uk-UA')}` : ''}
                    </div>
                  )}
                  {s.bankingVerificationStatus === 'Rejected' && (
                    <div style={{ fontSize: 11, color: 'var(--crm-danger)', marginTop: 3 }}>
                      Відхилив: {s.bankingVerifiedByUsername || 'уповноважений користувач'}
                      {s.bankingVerifiedAtUtc ? ` · ${new Date(s.bankingVerifiedAtUtc).toLocaleString('uk-UA')}` : ''}
                      {s.bankingRejectionReason ? <div>Причина: {s.bankingRejectionReason}</div> : null}
                    </div>
                  )}
                  {s.vatRate > 0 && <div style={{ fontSize: '12px', color: 'var(--crm-warning)', marginTop: '2px' }}>ПДВ: {s.vatRate}%</div>}
                  <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginTop: '2px' }}>Оплата: {s.paymentTermDays > 0 ? `${s.paymentTermDays} дн. відстрочки` : 'у день накладної'}</div>
                  {qualityBySupplier.has(s.id) && (
                    <div style={{ fontSize: 12, marginTop: 4, color: qualityBySupplier.get(s.id)!.riskLevel === 'normal' ? 'var(--crm-success)' : qualityBySupplier.get(s.id)!.riskLevel === 'warning' ? 'var(--crm-warning)' : 'var(--crm-danger)' }}>
                      Закупівельний рейтинг: <strong>{qualityBySupplier.get(s.id)!.procurementScore.toFixed(1)}</strong>
                      {qualityBySupplier.get(s.id)!.overdueClaimCount > 0 ? ` · прострочено претензій ${qualityBySupplier.get(s.id)!.overdueClaimCount}` : ''}
                      {qualityBySupplier.get(s.id)!.overduePurchaseOrderCount > 0 ? ` · прострочено поставок ${qualityBySupplier.get(s.id)!.overduePurchaseOrderCount}` : ''}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                    <button style={{ ...btn('info'), padding: '4px 8px', fontSize: '12px' }} onClick={e => { e.stopPropagation(); openEditSupplier(s); }}>✏️</button>
                    {canDeleteSupplier && (
                      <button style={{ ...btn('danger'), padding: '4px 8px', fontSize: '12px' }} onClick={e => { e.stopPropagation(); handleDeleteSupplier(s.id); }}>Вид.</button>
                    )}
                    <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)', marginLeft: 'auto', alignSelf: 'center' }}>
                      {supplierProducts.filter(sp => sp.supplierId === s.id).length} поз.
                    </span>
                  </div>
                </>
              )}
            </div>
          ))}
          {visibleSuppliers.length === 0 && !loading && <div style={{ color: 'var(--crm-text-muted)', textAlign: 'center', padding: '20px' }}>Постачальників за фільтром немає</div>}
        </div>

        <div className="suppliers-detail-panel" style={{ flex: 1 }}>
          {!activeSupplier ? (
            <div style={{ background: 'var(--crm-surface-alt)', borderRadius: 'var(--crm-radius-md)', padding: '40px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>
              ← Оберіть постачальника для перегляду прайс-листа
            </div>
          ) : (
            <>
              {activeQuality && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 14, background: 'var(--crm-info-bg)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', padding: 12 }}>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Рейтинг якості</div><strong style={{ color: activeQuality.qualityScore >= 90 ? 'var(--crm-success)' : activeQuality.qualityScore >= 70 ? 'var(--crm-warning)' : 'var(--crm-danger)' }}>{activeQuality.qualityScore.toFixed(1)} / 100</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Надходження / партії</div><strong>{activeQuality.receiptCount} / {activeQuality.totalLotCount}</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Проблемні партії</div><strong>{activeQuality.affectedLotCount}</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Зараз заблоковано</div><strong>{activeQuality.currentBlockedQuantity.toFixed(2)} · {activeQuality.currentBlockedValue.toFixed(2)} грн</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Прострочений залишок</div><strong>{activeQuality.expiredOpenQuantity.toFixed(2)}</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Обсяг поставок</div><strong>{activeQuality.receivedValue.toFixed(2)} грн</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Закупівельний рейтинг</div><strong style={{ color: activeQuality.riskLevel === 'normal' ? 'var(--crm-success)' : activeQuality.riskLevel === 'warning' ? 'var(--crm-warning)' : 'var(--crm-danger)' }}>{activeQuality.procurementScore.toFixed(1)} · {activeQuality.riskLevel === 'normal' ? 'низький ризик' : activeQuality.riskLevel === 'warning' ? 'увага' : 'критичний ризик'}</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Відкриті / прострочені претензії</div><strong>{activeQuality.openClaimCount} / {activeQuality.overdueClaimCount}</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Компенсовано</div><strong>{activeQuality.totalRecoveredAmount.toFixed(2)} із {activeQuality.totalClaimedAmount.toFixed(2)} грн · {activeQuality.recoveryRate.toFixed(1)}%</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Ордери / завершено</div><strong>{activeQuality.purchaseOrderCount} / {activeQuality.completedPurchaseOrderCount}</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Вчасність поставок</div><strong style={{ color: activeQuality.onTimeDeliveryRate >= 90 ? 'var(--crm-success)' : activeQuality.onTimeDeliveryRate >= 75 ? 'var(--crm-warning)' : 'var(--crm-danger)' }}>{activeQuality.onTimeDeliveryRate.toFixed(1)}%</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Прострочені ордери</div><strong style={{ color: activeQuality.overduePurchaseOrderCount > 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>{activeQuality.overduePurchaseOrderCount}</strong></div>
                  <div><div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Сер. затримка / ціна</div><strong>{activeQuality.averageDelayDays.toFixed(1)} дн. · {activeQuality.averagePriceVariancePercent > 0 ? '+' : ''}{activeQuality.averagePriceVariancePercent.toFixed(1)}%</strong></div>
                </div>
              )}
              <section style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', padding: 12, marginBottom: 14 }}>
                <h3 style={{ margin: '0 0 10px', color: 'var(--crm-text)' }}>Історія платіжних реквізитів</h3>
                {bankingHistory.length === 0 ? (
                  <div style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>Змін реквізитів ще не зафіксовано.</div>
                ) : (
                  <div style={{ overflowX: 'auto', maxHeight: 300 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={th}>Дата</th><th style={th}>Користувач</th><th style={th}>Джерело</th>
                        <th style={th}>Попередні реквізити</th><th style={th}>Нові реквізити</th><th style={th}>Рішення</th>
                      </tr></thead>
                      <tbody>
                        {bankingHistory.map(change => (
                          <tr key={change.id}>
                            <td style={td}>{new Date(change.changedAtUtc).toLocaleString('uk-UA')}</td>
                            <td style={td}>{change.changedByUsername || 'Система'}</td>
                            <td style={td}>{change.source === 'ExcelImport' ? 'Excel-імпорт' : 'Ручна зміна'}</td>
                            <td style={{ ...td, fontSize: 12 }}>
                              <div>{change.previousLegalName || '—'}</div>
                              <div>{change.previousTaxId || '—'}</div>
                              <div style={{ wordBreak: 'break-all' }}>{change.previousIban || '—'}</div>
                              <div>{change.previousBankName || '—'}</div>
                            </td>
                            <td style={{ ...td, fontSize: 12 }}>
                              <div>{change.newLegalName || '—'}</div>
                              <div>{change.newTaxId || '—'}</div>
                              <div style={{ wordBreak: 'break-all' }}>{change.newIban || '—'}</div>
                              <div>{change.newBankName || '—'}</div>
                            </td>
                            <td style={{ ...td, fontSize: 12 }}>
                              <div>{change.verificationStatus === 'Verified' ? 'Підтверджено' : change.verificationStatus === 'Rejected' ? 'Відхилено' : change.verificationStatus === 'PendingVerification' ? 'Очікує перевірки' : 'Не вимагалося'}</div>
                              {change.reviewedByUsername ? <div>{change.reviewedByUsername}</div> : null}
                              {change.reviewedAtUtc ? <div>{new Date(change.reviewedAtUtc).toLocaleString('uk-UA')}</div> : null}
                              {change.reviewedAtUtc ? (
                                <div style={{ color: change.reviewRiskLevel === 'Critical' ? 'var(--crm-danger)' : change.reviewRiskLevel === 'High' ? 'var(--crm-warning)' : 'var(--crm-text-muted)' }}>
                                  Ризик: {change.reviewRiskLevel === 'Critical' ? 'критичний' : change.reviewRiskLevel === 'High' ? 'високий' : 'стандартний'}
                                  {change.sensitiveChangesConfirmed ? ' · критичні поля звірено' : ''}
                                </div>
                              ) : null}
                              {change.rejectionReason ? <div style={{ color: 'var(--crm-danger)' }}>{change.rejectionReason}</div> : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
              <div style={{ border: '1px solid var(--crm-blocked)', borderRadius: 'var(--crm-radius-md)', padding: 12, marginBottom: 14, background: 'var(--crm-blocked-bg)' }}>
                <div className="supplier-quality-claims__header" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <strong style={{ color: 'var(--crm-blocked)', flex: 1 }}>Претензії за якістю</strong>
                  <span style={{ fontSize: 12, color: 'var(--crm-blocked)' }}>
                    заявлено {qualityClaims.reduce((sum, claim) => sum + claim.claimedAmount, 0).toFixed(2)} грн · компенсовано {qualityClaims.reduce((sum, claim) => sum + claim.recoveredAmount, 0).toFixed(2)} грн
                  </span>
                  <button style={btn('blocked')} onClick={() => setShowClaimForm(value => !value)}>+ Претензія</button>
                </div>
                {claimError && <div role="alert" style={{ color: 'var(--crm-danger)', fontSize: 12, marginBottom: 8 }}>{claimError}</div>}
                {showClaimForm && (
                  <form onSubmit={createClaim} className="supplier-quality-claim-form" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 2fr) repeat(3, minmax(120px, 1fr))', gap: 8, marginBottom: 12 }}>
                    <select
                      aria-label="Інцидент якості"
                      style={inp}
                      value={claimForm.qualityEventId}
                      onChange={e => {
                        const event = qualityIncidents.find(item => item.qualityEventId === Number(e.target.value));
                        setClaimForm(form => ({
                          ...form,
                          qualityEventId: e.target.value,
                          claimedAmount: event ? String(event.suggestedClaimAmount) : '',
                        }));
                      }}
                      required
                    >
                      <option value="">Оберіть інцидент…</option>
                      {qualityIncidents.filter(item => !item.hasActiveClaim).map(item => (
                        <option key={item.qualityEventId} value={item.qualityEventId}>
                          {item.productName} · {item.lotNumber || `накладна #${item.receiptId}`} · {item.reason}
                        </option>
                      ))}
                    </select>
                    <input aria-label="Сума претензії, гривень" style={inp} type="number" min="0.01" step="0.01" placeholder="Сума, грн" value={claimForm.claimedAmount} onChange={e => setClaimForm(form => ({ ...form, claimedAmount: e.target.value }))} required />
                    <input aria-label="Строк претензії" style={inp} type="date" value={claimForm.dueDate} onChange={e => setClaimForm(form => ({ ...form, dueDate: e.target.value }))} />
                    <input aria-label="Відповідальний за претензію" style={inp} placeholder="Відповідальний" value={claimForm.ownerName} onChange={e => setClaimForm(form => ({ ...form, ownerName: e.target.value }))} />
                    <input aria-label="Примітки до претензії" style={{ ...inp, gridColumn: '1 / -2' }} placeholder="Примітки до претензії" value={claimForm.notes} onChange={e => setClaimForm(form => ({ ...form, notes: e.target.value }))} />
                    <button type="submit" style={btn('success')}>Створити</button>
                  </form>
                )}
                {qualityClaims.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>Претензій ще немає.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 7 }}>
                    {qualityClaims.map(claim => (
                      <div key={claim.id} style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: 9, background: 'var(--crm-surface)', color: 'var(--crm-text)', fontSize: 12 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <strong>{claim.claimNumber}</strong>
                          <span>{claim.productName} · {claim.lotNumber || `накладна #${claim.receiptId}`}</span>
                          <span style={{ borderRadius: 10, padding: '2px 7px', background: claim.status === 'Settled' ? 'var(--crm-success-bg)' : claim.isOverdue ? 'var(--crm-danger-bg)' : 'var(--crm-blocked-bg)', color: claim.status === 'Settled' ? 'var(--crm-success)' : claim.isOverdue ? 'var(--crm-danger)' : 'var(--crm-blocked)' }}>
                            {claimStatusLabel(claim.status)}{claim.isOverdue ? ' · прострочено' : ''}
                          </span>
                          <span style={{ marginLeft: 'auto' }}>{claim.claimedAmount.toFixed(2)} грн{claim.recoveredAmount > 0 ? ` · повернено ${claim.recoveredAmount.toFixed(2)}` : ''}</span>
                        </div>
                        <div style={{ color: 'var(--crm-text-muted)', marginTop: 4 }}>
                          {claim.incidentReason}{claim.ownerName ? ` · відповідальний: ${claim.ownerName}` : ''}{claim.dueDate ? ` · до ${new Date(claim.dueDate).toLocaleDateString('uk-UA')}` : ''}
                        </div>
                        <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
                          {claim.status === 'Draft' && <button style={btn('info')} onClick={() => transitionClaim(claim, 'Submitted')}>Надіслати</button>}
                          {claim.status === 'Submitted' && <button style={btn('success')} onClick={() => transitionClaim(claim, 'Accepted')}>Визнано</button>}
                          {claim.status === 'Submitted' && <button style={btn('warning')} onClick={() => transitionClaim(claim, 'Rejected')}>Відхилено</button>}
                          {claim.status === 'Rejected' && <button style={btn('info')} onClick={() => transitionClaim(claim, 'Submitted')}>Надіслати повторно</button>}
                          {claim.status === 'Accepted' && <button style={btn('success')} onClick={() => transitionClaim(claim, 'Settled')}>Закрити з компенсацією</button>}
                          {!['Settled', 'Cancelled'].includes(claim.status) && <button style={btn('muted')} onClick={() => transitionClaim(claim, 'Cancelled')}>Скасувати</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="suppliers-price-toolbar" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, color: 'var(--crm-text)', flex: 1 }}>Прайс-лист: {activeSupplier.name}</h3>
                <input style={{ ...inp, width: '200px' }} placeholder='Пошук...' value={spSearch} onChange={e => setSpSearch(e.target.value)} />
                <select style={{ ...inp, width: '220px' }} value={spSort} onChange={(e) => setSpSort(e.target.value as SupplierProductSort)}>
                  <option value='name-asc'>Сортування: Назва А-Я</option>
                  <option value='name-desc'>Сортування: Назва Я-А</option>
                  <option value='price-asc'>Сортування: Ціна ↑</option>
                  <option value='price-desc'>Сортування: Ціна ↓</option>
                  <option value='effective-asc'>Сортування: Ефективна ціна ↑</option>
                  <option value='effective-desc'>Сортування: Ефективна ціна ↓</option>
                  <option value='code-asc'>Сортування: Код А-Я</option>
                  <option value='code-desc'>Сортування: Код Я-А</option>
                </select>
                <button style={btn('success')} onClick={() => { setShowNewSp(v => !v); setEditingSpId(null); }}>+ Позиція</button>
                <button style={btn('blocked')} onClick={handleExport}>⬇ Excel</button>
                <button style={btn('warning')} onClick={() => fileRef.current?.click()}>⬆ Імпорт</button>
                <input ref={fileRef} type='file' accept='.xlsx,.xls' style={{ display: 'none' }} onChange={handleImportFile} />
              </div>
              {importMsg && <div role="status" style={{ background: 'var(--crm-info-bg)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '8px 12px', marginBottom: '12px', fontSize: '13px', color: 'var(--crm-info)' }}>{importMsg}</div>}
              <div style={{ marginBottom: '10px', fontSize: '13px', color: 'var(--crm-text-muted)' }}>
                Показано {spPageStart}-{spPageEnd} із {sortedSp.length} позицій
              </div>
              {showNewSp && (
                <form onSubmit={handleCreateSp} style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px', marginBottom: '8px' }}>
                    <label style={{ fontSize: '12px' }}>Назва *<input style={inp} value={spForm.supplierProductName} onChange={e => setSpForm(f => ({ ...f, supplierProductName: e.target.value }))} required /></label>
                    <label style={{ fontSize: '12px' }}>Код<input style={inp} value={spForm.supplierCode} onChange={e => setSpForm(f => ({ ...f, supplierCode: e.target.value }))} /></label>
                    <label style={{ fontSize: '12px' }}>Один.<select style={inp} value={spForm.supplierWeightType} onChange={e => setSpForm(f => ({ ...f, supplierWeightType: e.target.value }))}>{WEIGHT_TYPES.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}</select></label>
                    <label style={{ fontSize: '12px' }}>Ціна (₴)<input style={inp} type='number' step='0.01' min='0' value={spForm.supplierUnitPrice} onChange={e => setSpForm(f => ({ ...f, supplierUnitPrice: e.target.value }))} required /></label>
                    <label style={{ fontSize: '12px' }}>Коефіцієнт<input style={inp} type='number' step='0.001' min='0.001' value={spForm.conversionFactor} onChange={e => setSpForm(f => ({ ...f, conversionFactor: e.target.value }))} /></label>
                    <label style={{ fontSize: '12px' }}>Внутрішній продукт
                      <div style={{ marginTop: '2px' }}>
                        <SearchSelect
                          items={productSearchItems}
                          value={spForm.internalProductId}
                          onChange={(id) => setSpForm((f) => ({ ...f, internalProductId: id }))}
                          placeholder="🔍 Не прив’язано / пошук..."
                          minWidth={0}
                        />
                      </div>
                    </label>
                  </div>
                  <input style={{ ...inp, marginBottom: '8px' }} placeholder='Примітки' value={spForm.notes} onChange={e => setSpForm(f => ({ ...f, notes: e.target.value }))} />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type='submit' style={btn('success')}>Зберегти</button>
                    <button type='button' style={btn('muted')} onClick={() => { setShowNewSp(false); setSpForm({ ...emptySpForm }); }}>Скасувати</button>
                  </div>
                </form>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={th}>Код</th><th style={th}>Назва у постачальника</th>
                      <th style={th}>Один.</th><th style={th}>Ціна (₴)</th>
                      <th style={th}>Коеф.</th><th style={th}>Ефект. ціна</th>
                      <th style={th}>Внутрішній продукт</th>
                      <th style={th}>Актив.</th><th style={th}>Примітки</th><th style={th}>Дії</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSp.length === 0 && <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)', padding: '30px' }}>Позицій немає. Додайте вручну або імпортуйте Excel.</td></tr>}
                    {pagedSp.map(sp => {
                      if (editingSpId === sp.id) return (
                        <tr key={sp.id} style={{ background: 'var(--crm-warning-bg)' }}>
                          <td style={td}><input style={inp} value={editSpForm.supplierCode} onChange={e => setEditSpForm(f => ({ ...f, supplierCode: e.target.value }))} /></td>
                          <td style={td}><input style={inp} value={editSpForm.supplierProductName} onChange={e => setEditSpForm(f => ({ ...f, supplierProductName: e.target.value }))} /></td>
                          <td style={td}><select style={inp} value={editSpForm.supplierWeightType} onChange={e => setEditSpForm(f => ({ ...f, supplierWeightType: e.target.value }))}>{WEIGHT_TYPES.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}</select></td>
                          <td style={td}><input style={inp} type='number' step='0.01' min='0' value={editSpForm.supplierUnitPrice} onChange={e => setEditSpForm(f => ({ ...f, supplierUnitPrice: e.target.value }))} /></td>
                          <td style={td}><input style={inp} type='number' step='0.001' min='0.001' value={editSpForm.conversionFactor} onChange={e => setEditSpForm(f => ({ ...f, conversionFactor: e.target.value }))} /></td>
                          <td style={{ ...td, color: 'var(--crm-text-muted)' }}>{((parseFloat(editSpForm.supplierUnitPrice) || 0) / (parseFloat(editSpForm.conversionFactor) || 1)).toFixed(2)} ₴</td>
                          <td style={td}>
                            <SearchSelect
                              items={productSearchItems}
                              value={editSpForm.internalProductId}
                              onChange={(id) => setEditSpForm((f) => ({ ...f, internalProductId: id }))}
                              placeholder="🔍 Пошук продукту..."
                              minWidth={0}
                            />
                          </td>
                          <td style={td}><input type='checkbox' checked={editSpForm.isActive} onChange={e => setEditSpForm(f => ({ ...f, isActive: e.target.checked }))} /></td>
                          <td style={td}><input style={inp} value={editSpForm.notes} onChange={e => setEditSpForm(f => ({ ...f, notes: e.target.value }))} /></td>
                          <td style={td}>
                            <button style={btn('info')} onClick={() => handleUpdateSp(sp)}>Зберегти</button>{' '}
                            <button style={btn('muted')} onClick={() => setEditingSpId(null)}>✕</button>
                          </td>
                        </tr>
                      );
                      const isBest = (() => {
                        if (!sp.internalProductId) return false;
                        const linked = supplierProducts.filter(x => x.internalProductId === sp.internalProductId && x.isActive && x.conversionFactor > 0);
                        if (linked.length < 2) return false;
                        const rank = (item: SupplierProduct) => {
                          const profile = qualityBySupplier.get(item.supplierId);
                          const riskRank = profile?.riskLevel === 'critical' ? 2 : profile?.riskLevel === 'warning' ? 1 : 0;
                          const score = profile?.procurementScore ?? 100;
                          const adjustedPrice = item.effectiveUnitPrice * (
                            1 + (100 - score) / 100 * 0.2 + (profile?.riskLevel === 'critical' ? 0.15 : 0)
                          );
                          return { riskRank, adjustedPrice };
                        };
                        const recommended = [...linked].sort((a, b) => {
                          const aRank = rank(a);
                          const bRank = rank(b);
                          return aRank.riskRank - bRank.riskRank ||
                            aRank.adjustedPrice - bRank.adjustedPrice ||
                            a.effectiveUnitPrice - b.effectiveUnitPrice;
                        })[0];
                        return recommended.id === sp.id;
                      })();
                      return (
                        <tr key={sp.id} style={{ background: isBest ? 'var(--crm-success-bg)' : 'var(--crm-surface)' }}>
                          <td style={td}>{sp.supplierCode || '—'}</td>
                          <td style={td}>{sp.supplierProductName}{isBest && <span title='Рекомендовано з урахуванням ціни та якості' style={{ marginLeft: '6px' }}>⭐</span>}</td>
                          <td style={td}>{WEIGHT_TYPES.find(w => w.value === sp.supplierWeightType)?.label ?? sp.supplierWeightType}</td>
                          <td style={td}>{sp.supplierUnitPrice.toFixed(2)} ₴</td>
                          <td style={td}>{sp.conversionFactor}</td>
                          <td style={{ ...td, fontWeight: 600, color: isBest ? 'var(--crm-success)' : 'var(--crm-text)' }}>{sp.effectiveUnitPrice.toFixed(2)} ₴</td>
                          <td style={td}>{sp.internalProductName
                            ? <span style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '2px 6px', fontSize: '12px' }}>{sp.internalProductName}</span>
                            : <span style={{ color: 'var(--crm-text-muted)' }}>не прив’язано</span>}
                          </td>
                          <td style={{ ...td, textAlign: 'center' }}>{sp.isActive ? '✅' : '❌'}</td>
                          <td style={{ ...td, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--crm-text-muted)' }}>{sp.notes || '—'}</td>
                          <td style={td}>
                            <button style={{ ...btn('info'), padding: '4px 8px' }} onClick={() => openEditSp(sp)}>✏️</button>{' '}
                            <button style={{ ...btn('danger'), padding: '4px 8px' }} onClick={() => handleDeleteSp(sp.id)}>Вид.</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {sortedSp.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                  <button style={btn('muted')} onClick={() => setSpPage((p) => Math.max(1, p - 1))} disabled={safeSpPage <= 1}>← Назад</button>
                  <span style={{ fontSize: '13px', color: 'var(--crm-text)' }}>Сторінка {safeSpPage} / {totalSpPages}</span>
                  <button style={btn('muted')} onClick={() => setSpPage((p) => Math.min(totalSpPages, p + 1))} disabled={safeSpPage >= totalSpPages}>Вперед →</button>
                </div>
              )}
              {filteredSp.some(sp => sp.internalProductId) && (
                <div style={{ marginTop: '16px', padding: '12px', background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-md)', fontSize: '12px', color: 'var(--crm-warning)' }}>
                  ⭐ — рекомендований постачальник з урахуванням ціни, якості партій і претензій
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuppliersPage;
